import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FreeResourcesBar from '../components/FreeResourcesBar';
import { CheckCircle, Award, Clock, Users, Calendar, Download, ChevronDown, ChevronUp, Target } from 'lucide-react';

const ProductManagementAIPage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  const syllabus = [
    { week: "Week 1", title: "The Role of AI Product Manager + Industry Landscape", topics: ["AI PM vs traditional PM", "AI product lifecycle and market fit", "Business, data, & engineering balance", "Industry trends and key players"] },
    { week: "Week 2", title: "Defining and Framing AI Problems", topics: ["Business problem identification", "When AI adds value vs when it doesn't", "Feasibility and impact prioritization", "Risk-aware problem statements"] },
    { week: "Week 3", title: "Data in AI Products and Use Cases", topics: ["Data quality and governance", "Common AI product types", "Dataset design & annotation", "Data licensing & ethical considerations"] },
    { week: "Week 4", title: "AI Concepts for Product Managers", topics: ["Supervised, unsupervised, & reinforcement learning", "Model training and validation", "Model outputs & limitations", "Ethical considerations"] },
    { week: "Week 5", title: "Generative AI and Prompt Engineering", topics: ["Understanding generative AI capabilities", "Prompt engineering fundamentals", "Iterative AI development", "AI-driven UX design"] },
    { week: "Week 6", title: "Exploring Generative AI Opportunities", topics: ["Retrieval-Augmented Generation (RAG)", "Agents & multi-step AI workflows", "Business value & use cases", "Self-driving systems case study"] },
    { week: "Week 7", title: "Measuring Business Impact for AI Products", topics: ["Core AI KPIs", "Experimentation lifecycle", "KPI alignment with objectives", "Business and financial fundamentals"] },
    { week: "Week 8", title: "Responsible AI and Governance", topics: ["Bias mitigation and privacy", "Governance & legal considerations", "Privacy and fairness auditing", "Building user trust"] },
    { week: "Week 9", title: "Execution and Go-To-Market Strategy", topics: ["Agile & iterative development", "Cross-functional communication", "GTM planning", "Market launch strategies"] },
    { week: "Week 10", title: "AI Teams, Deployment, and Post-Launch Monitoring", topics: ["Collaborating with AI teams", "Deployment & scaling", "Performance monitoring", "Continuous improvement"] },
    { week: "Week 11", title: "Future Trends in AI Product Management", topics: ["Agentic AI & multi-agent systems", "AI personalization", "Evolving roles of AI PMs", "Building your AI PM career path"] },
    { week: "Week 12", title: "Capstone Showcase and Career Guidance", topics: ["Final project presentations", "Portfolio building with AI tools", "Resume and career advice", "Continuous learning paths"] }
  ];

  const faqs = [
    {
      id: 1,
      question: "What is Product Management for AI & ML?",
      answer: "This 6-week intensive course teaches you how to manage AI and ML-driven products from conception to launch. You'll learn to bridge business strategy, engineering, and data science while building AI products that deliver real business value."
    },
    {
      id: 2,
      question: "Who is this course for?",
      answer: "Perfect for Product Managers looking to transition into AI roles, Junior AI PMs seeking career advancement, and professionals with PM experience who want to work on AI/ML products. Basic product management understanding is helpful but not required."
    },
    {
      id: 3,
      question: "Do I need technical or coding background?",
      answer: "No coding required! This course is designed to give you the conceptual understanding and frameworks needed to lead AI products effectively. You'll learn to communicate with technical teams without needing to code yourself."
    },
    {
      id: 4,
      question: "What's the capstone project?",
      answer: "Your capstone is an end-to-end AI product strategy pitch deck. You'll define a problem, design an AI solution, map user value and business KPIs, and create a metrics optimization plan. This becomes a portfolio-ready showcase of your AI PM abilities."
    },
    {
      id: 5,
      question: "What's the time commitment?",
      answer: "The course runs for 6 weeks with live sessions twice per week (Tuesdays & Thursdays). Expect to dedicate 8-10 hours per week including class time, assignments, case studies, and capstone project work."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <FreeResourcesBar />

      {/* Hero Section - Elegant Palette */}
      <section className="pt-24 pb-16 bg-[#1E293B] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#4F46E5] text-white px-6 py-2 rounded-full font-bold mb-6">
              <Target className="w-5 h-5" />
              <span>6-Week Intensive Program</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
              Product Management for AI & ML
            </h1>

            {/* Description */}
            <p className="text-xl md:text-2xl text-slate-700 mb-8 leading-relaxed">
              Build smart products and a smarter career. Learn how to turn AI & ML potential into real-world impact through strategy, data, and product vision.
            </p>

            <p className="text-lg text-slate-600 mb-8">
              <strong>Led by industry expert with 22+ years AI & product experience</strong>
            </p>

            {/* Key Info Badges */}
            <div className="flex flex-wrap gap-4 justify-center mb-8">
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl border border-white/20">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span className="font-semibold">Starts January 22, 2026</span>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl border border-white/20">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span className="font-semibold">6 weeks | Tuesdays & Thursdays</span>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl border border-white/20">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  <span className="font-semibold">5:30 PM AEDT</span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.href = '/contact'}
                className="bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold px-10 py-4 rounded-xl shadow-lg transition-all duration-300"
              >
                Apply Now
              </button>
              <button className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-bold px-10 py-4 rounded-xl border border-white/20 transition-all duration-300">
                <Download className="w-5 h-5 inline mr-2" />
                Download Course Info
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Course Is For */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              This course is for you, if...
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                title: "YOU ARE A PRODUCT MANAGER ASPIRING FOR AN AI ROLE", 
                desc: "Close the gap in your AI knowledge and skills. Build competence in state-of-the-art AI and ML techniques, gain clarity on the AI landscape, and learn how to solve user problems with AI while driving real business outcomes."
              },
              { 
                title: "YOU ARE A JUNIOR AI/ML PM LOOKING FOR A CAREER BOOST", 
                desc: "Bridge the experience gap. Learn strategic AI applications through industry best practices. The practical focus, including market research, prototyping, and data-driven decision-making, guarantees tangible career growth."
              },
              { 
                title: "YOU HAVE PM COMPETENCE AND WANT TO WORK ON AI/ML PRODUCTS", 
                desc: "Transition seamlessly into AI/ML PM. Perfect for professionals in various roles, from managers to data scientists to business owners. Gain essential technical knowledge and learn to contribute confidently to AI initiatives."
              }
            ].map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-slate-50 to-purple-50 rounded-2xl p-8 hover:shadow-xl transition-shadow">
                <h3 className="text-lg font-bold text-purple-700 mb-4">{item.title}</h3>
                <p className="text-slate-700">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You'll Do */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              What You'll Do
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg mx-auto">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3 text-center">IN-DEPTH LEARNING</h3>
              <p className="text-slate-600 text-center">Dive deep into the full lifecycle of AI product management: from framing problems to shipping intelligent solutions. Build practical skills and frameworks to go from AI-curious to AI-confident.</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg mx-auto">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3 text-center">REAL-WORLD PRACTICE</h3>
              <p className="text-slate-600 text-center">Work on workshops, case studies, and guest sessions from AI leaders. Analyze systems behind Spotify recommendations, ChatGPT, and autonomous vehicles while building your own strategies.</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg mx-auto">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3 text-center">CAPSTONE PROJECT</h3>
              <p className="text-slate-600 text-center">Create a full end-to-end AI product pitch deck — defining the problem, AI solution, user value, and business KPIs. Build a portfolio-ready project with metrics and optimization plan.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Syllabus */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Course Syllabus</h2>
            <p className="text-xl text-slate-600">12 weeks of intensive, hands-on learning</p>
          </div>

          <div className="space-y-4">
            {syllabus.map((item, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center flex-shrink-0 text-white font-bold">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm font-bold text-purple-700">{item.week}</span>
                      <h3 className="text-xl font-bold text-white">{item.title}</h3>
                    </div>
                    <ul className="space-y-1">
                      {item.topics.map((topic, topicIdx) => (
                        <li key={topicIdx} className="flex items-center gap-2 text-slate-600">
                          <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0" />
                          <span>{topic}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button className="bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold px-10 py-4 rounded-xl shadow-lg transition-all duration-300">
              <Download className="w-5 h-5 inline mr-2" />
              Download full syllabus
            </button>
          </div>
        </div>
      </section>

      {/* Companies */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-8">Where Our Students Can Work</h3>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
            <img src="https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg" alt="Google" className="h-8" />
            <img src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg" alt="Apple" className="h-8" />
            <img src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg" alt="Microsoft" className="h-8" />
            <img src="https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg" alt="Amazon" className="h-10" />
            <img src="https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg" alt="Meta" className="h-8" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Frequently asked questions</h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.id} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-slate-900">{faq.question}</span>
                  {openFAQ === faq.id ? (
                    <ChevronUp className="w-5 h-5 text-[#4F46E5] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#4F46E5] flex-shrink-0" />
                  )}
                </button>
                {openFAQ === faq.id && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                    <p className="text-slate-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#1E293B] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to lead AI product innovation?
          </h2>
          <p className="text-xl text-slate-700 mb-8">Starting January 22, 2026 | 6 weeks | Tuesdays & Thursdays</p>
          <button
            onClick={() => window.location.href = '/contact'}
            className="bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold px-12 py-5 rounded-xl shadow-2xl transition-all duration-300 text-lg"
          >
            Apply Now →
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductManagementAIPage;
